import { Checkbox } from '@/components/ui/checkbox';
import { RemoveButton } from '@/modules/back-testing/datasets/ui/remove-button';
import { FieldInput, FieldSelect } from '@/modules/simulator/ui/field';
import type { CustomToolInputType } from '@/types/custom-tool.types';
import { useCustomToolBuilder } from '../builder/builder-context';
import { INPUT_TYPE_LABELS, inputNameProblem, withInputChanged, withoutInput } from '../custom-tool-draft';

const TYPE_OPTIONS = Object.entries(INPUT_TYPE_LABELS).map(([value, label]) => ({ value, label }));

/** One input the AI passes. Renaming it keeps the arguments that use it; removing it clears them. */
export const InputEditor = ({ index, onRemoved }: { index: number; onRemoved?: () => void }) => {
	const { draft, change } = useCustomToolBuilder();
	const input = draft.inputs[index];
	if (!input) return null;
	const edit = (patch: Partial<typeof input>) => change((current) => withInputChanged(current, index, patch), `input:${index}`);
	const problem = inputNameProblem(draft.inputs, index);

	return (
		<div role='group' aria-label={`Input ${input.name}`} className='flex flex-col gap-2'>
			<div className='flex items-center gap-2'>
				<FieldInput aria-label='Input name' className='font-mono' value={input.name} onChange={(event) => edit({ name: event.target.value })} />
				<RemoveButton
					label='Remove input'
					onClick={() => {
						change((current) => withoutInput(current, index));
						onRemoved?.();
					}}
				/>
			</div>
			{problem && <p className='text-[11px] leading-4 text-red-600'>{problem}</p>}
			<div className='flex items-center gap-3'>
				<div className='w-32'>
					<FieldSelect
						aria-label='Input type'
						value={input.type}
						options={TYPE_OPTIONS}
						onChange={(type) => edit({ type: type as CustomToolInputType })}
					/>
				</div>
				<label className='flex items-center gap-1.5 text-xs text-gray-900'>
					<Checkbox checked={input.required} onCheckedChange={(checked) => edit({ required: checked === true })} />
					Required
				</label>
			</div>
			<FieldInput
				aria-label='Input description'
				placeholder='What the AI should pass'
				value={input.description}
				onChange={(event) => edit({ description: event.target.value })}
			/>
		</div>
	);
};
